import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Anchor, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../../core/auth'

const POST_LOGIN_PATH = '/lng-ops'

export default function Login() {
  const navigate = useNavigate()
  const { user, login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user) navigate(POST_LOGIN_PATH, { replace: true })
  }, [user, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      navigate(POST_LOGIN_PATH)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-bg-canvas">
      <BrandingPanel />
      <FormPanel>
        <LoginForm
          email={email}
          password={password}
          showPassword={showPassword}
          error={error}
          loading={loading}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onTogglePassword={() => setShowPassword((v) => !v)}
          onSubmit={handleSubmit}
        />
      </FormPanel>
    </div>
  )
}

function BrandingPanel() {
  return (
    <div className="relative hidden flex-col items-center justify-center overflow-hidden p-10 lg:flex lg:w-3/5">
      {/* Subtle gradient backdrop */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 20% 30%, rgba(209,176,122,0.07) 0%, transparent 60%), ' +
            'radial-gradient(ellipse at 80% 80%, rgba(209,176,122,0.04) 0%, transparent 50%)',
        }}
      />
      {/* Grid texture overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'linear-gradient(rgba(209,176,122,0.04) 1px, transparent 1px), ' +
            'linear-gradient(90deg, rgba(209,176,122,0.04) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative flex items-center gap-3.5">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-muted">
          <Anchor className="h-6 w-6 text-accent" />
        </div>
        <span className="font-display text-3xl font-semibold text-text-primary">LNG Performance</span>
      </div>
    </div>
  )
}

function FormPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex w-full flex-col items-center justify-center border-l border-border bg-bg-surface px-8 py-12 lg:w-2/5">
      {/* Mobile logo — hidden on desktop where BrandingPanel shows */}
      <div className="mb-8 flex flex-col items-center lg:hidden">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-muted">
          <Anchor className="h-5 w-5 text-accent" />
        </div>
        <span className="mt-3 font-display text-xl font-semibold text-text-primary">LNG Performance</span>
      </div>
      <div className="w-full max-w-sm">{children}</div>
    </div>
  )
}

interface LoginFormProps {
  email: string
  password: string
  showPassword: boolean
  error: string
  loading: boolean
  onEmailChange: (v: string) => void
  onPasswordChange: (v: string) => void
  onTogglePassword: () => void
  onSubmit: (e: React.FormEvent) => void
}

function LoginForm({
  email,
  password,
  showPassword,
  error,
  loading,
  onEmailChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
}: LoginFormProps) {
  return (
    <div>
      <div className="mb-7">
        <h1 className="font-display text-2xl font-semibold text-text-primary">Welcome back</h1>
        <p className="mt-1.5 text-sm text-text-muted">Sign in to your LNG Performance account</p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <label className="hero-label mb-1.5 block">Email</label>
          <input
            type="text"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@company.com"
            className="input h-9 w-full"
            required
            autoFocus
          />
        </div>

        <div>
          <label className="hero-label mb-1.5 block">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder="Enter password"
              className="input h-9 w-full pr-10"
              required
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && <p className="text-xs text-negative">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary mt-1 h-10 w-full"
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="mt-6 text-center text-xs text-text-muted">
        Don&rsquo;t have an account?{' '}
        <Link to="/signup" className="font-medium text-accent hover:text-accent-hover">
          Start a free trial
        </Link>
      </p>
    </div>
  )
}
